/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import MuiTextField, { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';


import NumberFormat, { NumberFormatProps, NumberFormatValues } from 'react-number-format';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import { FieldRenderProps } from 'react-final-form';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

export const THREE_DIGIT_POSITIVE_INTEGER = {
    decimalScale: 0,
    allowNegative: false,
    isAllowed: (values: NumberFormatValues) => {
        if (!values.floatValue) {
            return true;
        }

        if (values.floatValue < 1000) {
            return true;
        }

        return false;
    }
}

export const FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE = {
    decimalScale: 2,
    allowNegative: false,
    isAllowed: (values: NumberFormatValues) => {
        if (!values.floatValue) {
            return true;
        }

        if (values.floatValue < 100000) {
            return true;
        }

        return false;
    }
}

export const PERCENTAGE = {
    decimalScale: 2,
    allowNegative: false,
    isAllowed: (values: NumberFormatValues) => {
        if (!values.floatValue) {
            return true;
        }

        if (values.floatValue <= 100) {
            return true;
        }

        return false;
    }
}

export const PERCENTAGE_NO_DECIMAL = {
    decimalScale: 0,
    allowNegative: false,
    isAllowed: (values: NumberFormatValues) => {
        if (!values.floatValue) {
            return true;
        }

        if (values.floatValue <= 100) {
            return true;
        }

        return false;
    }
}

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

type TextFieldsProps = Partial<Omit<MuiTextFieldProps, 'type'>> & FieldRenderProps<any, any> & { numberFormat: Omit<NumberFormatProps, 'format'> & { format?: (val: number | string | undefined) => number | string | undefined} }

interface INumericField extends TextFieldsProps {
}

interface IInternalNumberFormatProps {
    inputRef: (instance: NumberFormat | null) => void;
    onChange: (event: { target: { value: number | undefined } }) => void;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */
const InternalNumberFormat: React.FunctionComponent<IInternalNumberFormatProps> = ({
    inputRef,
    onChange,
    ...rest
}) => {
    const onValueChange = React.useCallback((values: NumberFormatValues) => {
        onChange({
            target: {
                value: values.floatValue,
            },
        })
    }, [onChange]);

    return (
        <NumberFormat
            {...rest}
            getInputRef={inputRef}
            onValueChange={onValueChange}
        />
    );
}


const NumericField: React.FunctionComponent<INumericField> = ({
    input: { name, value, onChange: onInputChange, checked, ...restInput },
    meta,
    fullWidth,
    onChange,
    InputProps,
    numberFormat,
    ...rest
}) => {

    const onChangeCombined = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (onChange) {
            onChange(event);
        }

        onInputChange(event);
    }, [onChange, onInputChange]);

    return (
        <MuiTextField
            {...rest as any}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            InputProps={{
                ...InputProps,
                inputComponent: InputProps?.inputComponent === undefined ? InternalNumberFormat : InputProps?.inputComponent
            }}
            inputProps={{ ...restInput, ...numberFormat }}
            name={name}
            value={value}
            onChange={onChangeCombined}
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default NumericField;
